import React, { useState, useEffect, useRef, useCallback } from 'react';
import io from 'socket.io-client';
import ChatService from '../../services/chat.service';
import AuthService from '../../services/auth.service';
import authHeader from '../../services/auth-header';
import { API_URL, CHAT_API_URL } from '../../services/config';
import { Row, Col, Button, Badge } from 'reactstrap';

function useSocket(token) {
  const socket = React.useMemo(() => 
    io(API_URL.replace(/\/api\/?$/, ''), { 
      autoConnect: false, 
      auth: { token } 
    }), [token]);
  
  React.useEffect(() => {
    if (token) {
      socket.connect();
      return () => socket.disconnect();
    }
  }, [socket, token]);
  
  return socket;
}

export default function Chat() {
  const token = authHeader().Authorization?.replace('Bearer ', '');
  const socket = useSocket(token);
  const [users, setUsers] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(-1);
  const [unreadCounts, setUnreadCounts] = useState({});
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const messagesEndRef = useRef(null);
  const fileRef = useRef(null); // file input ref

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadMessages = useCallback(async (conversationId) => {
    try {
      const msgs = await ChatService.getMessages(conversationId);
      setMessages(msgs);
      scrollToBottom();
    } catch (error) {
      console.error('Error loading messages:', error);
    }
  }, []);

  const loadUsers = async () => {
    try {
      const response = await AuthService.getAll();
      setUsers(response.data);
    } catch (error) {
      console.error('Error loading users:', error);
    }
  };

  const loadUnreadCounts = async () => {
    try {
      const response = await ChatService.getUnreadCounts();
      setUnreadCounts(response);
    } catch (error) {
      console.error('Error loading unread counts:', error);
    }
  };

  useEffect(() => {
    loadUsers();
    loadUnreadCounts();
    
    // Refresh unread counts every 30 seconds
    const interval = setInterval(loadUnreadCounts, 30000);
    return () => clearInterval(interval);
  }, []);

  // Refresh messages every 5 seconds when a conversation is selected
  useEffect(() => {
    if (!selectedConversation) return;
    
    const refreshInterval = setInterval(async () => {
      try {
        const msgs = await ChatService.getMessages(selectedConversation.id);
        setMessages(msgs);
        console.log('Admin messages refreshed via interval');
      } catch (error) {
        console.error('Error refreshing admin messages:', error);
      }
    }, 5000);
    
    return () => clearInterval(refreshInterval);
  }, [selectedConversation]);

  useEffect(() => {
    if (selectedConversation) {
      loadMessages(selectedConversation.id);
    }
  }, [selectedConversation, loadMessages]);

  useEffect(() => {
    if (!socket) return;
    
    const onNewMessage = (msg) => {
      console.log('Admin received new message:', msg);
      
      // Update unread counts immediately when new message arrives
      if (msg.senderId !== currentUser?.id) {
        // Find the user ID for this conversation by looking at the selected conversation
        // or by finding the user who has this conversation
        let userId = null;
        
        if (selectedConversation && msg.conversationId === selectedConversation.id) {
          userId = selectedConversation.clientId;
        } else {
          // If not the current conversation, we need to find the user
          // For now, we'll refresh the unread counts from the server
          loadUnreadCounts();
          return;
        }
        
        if (userId) {
          setUnreadCounts(prev => ({
            ...prev,
            [userId]: (prev[userId] || 0) + 1
          }));
        }
      }
      
      if (selectedConversation && msg.conversationId === selectedConversation.id) {
        setMessages(prev => {
          // Check if message already exists to avoid duplicates
          const exists = prev.some(m => m.id === msg.id);
          if (exists) return prev;
          
          // If this is a message from the current admin, replace any temporary message with same content
          if (msg.senderId === selectedConversation.adminId) {
            // Remove temporary message with same content and add the real one
            const filtered = prev.filter(m => !(m.isTemp && m.content === msg.content));
            console.log('Admin replacing temporary message with real message:', msg);
            return [...filtered, msg];
          }
          
          return [...prev, msg];
        });
        setTimeout(scrollToBottom, 50);
      }
    };

    const onRead = (data) => {
      if (selectedConversation && data.conversationId === selectedConversation.id) {
        // Handle read receipt if needed
      }
    };

    socket.on('message:new', onNewMessage);
    socket.on('message:read', onRead);

    return () => {
      socket.off('message:new', onNewMessage);
      socket.off('message:read', onRead);
    };
  }, [socket, selectedConversation, currentUser?.id]);

  const setActiveUser = async (user, index) => {
    setCurrentUser(user);
    setCurrentIndex(index);
    
    try {
      // Try to get existing conversation or create new one
      const conversation = await ChatService.initiateChatWithUser(user.id);
      setSelectedConversation(conversation);
      
      // Clear unread count for this user when opening conversation
      setUnreadCounts(prev => ({
        ...prev,
        [user.id]: 0
      }));
      
      // Mark messages as read when opening conversation
      if (conversation.id) {
        try {
          await ChatService.markRead(conversation.id);
        } catch (error) {
          console.error('Error marking messages as read:', error);
        }
      }
    } catch (error) {
      console.error('Error initiating chat with user:', error);
    }
  };

  const deleteChatHistory = async (userId, userName) => {
    if (window.confirm(`Are you sure you want to delete all chat history with ${userName}? This action cannot be undone.`)) {
      try {
        await ChatService.deleteChatHistory(userId);
        alert('Chat history deleted successfully');
        // Refresh the user list and unread counts
        loadUsers();
        loadUnreadCounts();
        // Clear current conversation if it was with this user
        if (currentUser?.id === userId) {
          setCurrentUser(null);
          setCurrentIndex(-1);
          setSelectedConversation(null);
          setMessages([]);
        }
      } catch (error) {
        console.error('Error deleting chat history:', error);
        alert('Error deleting chat history');
      }
    }
  };


  const sendMessage = async () => {
    if (!newMessage.trim() || !selectedConversation) return;
    
    const messageText = newMessage.trim();
    setNewMessage(''); // Clear input immediately
    
    // Create a temporary message to show immediately
    const tempMessage = {
      id: `temp-${Date.now()}`, // Temporary ID
      conversationId: selectedConversation.id,
      senderId: selectedConversation.adminId, // Admin's ID
      type: 'text',
      content: messageText,
      createdAt: new Date().toISOString(),
      isRead: true,
      isTemp: true // Flag to identify temporary messages
    };
    
    // Add temporary message immediately for instant feedback
    setMessages(prev => [...prev, tempMessage]);
    setTimeout(scrollToBottom, 50);
    
    // Set a timeout to remove temporary message if it doesn't get replaced
    const tempTimeout = setTimeout(() => {
      setMessages(prev => prev.filter(msg => msg.id !== tempMessage.id));
      console.log('Admin temporary message timeout - removing:', tempMessage.id);
    }, 10000); // 10 second timeout
    
    try {
      await ChatService.sendMessage(selectedConversation.id, messageText);
      // Clear the timeout since message was sent successfully
      clearTimeout(tempTimeout);
      
      // Mark messages as read after sending (to ensure proper unread count management)
      try {
        await ChatService.markRead(selectedConversation.id);
      } catch (readError) {
        console.error('Error marking messages as read after sending:', readError);
      }
    } catch (error) {
      console.error('Error sending message:', error);
      setNewMessage(messageText); // Restore text if sending failed
      // Remove the temporary message if sending failed
      setMessages(prev => prev.filter(msg => msg.id !== tempMessage.id));
      clearTimeout(tempTimeout);
    }
  };


  //  FILE UPLOAD FUNCTIONALTY
  const uploadFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedConversation) return;

    try {
      await ChatService.upload(selectedConversation.id, file);
      // Don't add to messages here - let socket event handle it
      fileRef.current.value = ''; // Clear file input
      setTimeout(scrollToBottom, 50);
    } catch (error) {
      console.error('Error uploading file:', error);
      alert('Error uploading file. Please try again.');
    }
  };



  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

 

  return (
    <div className="content">
      <style jsx>{`
        .list-group-item {
          border: 1px solid #dee2e6;
          border-radius: 8px;
          margin-bottom: 8px;
          transition: all 0.3s ease;
        }
        .list-group-item:hover {
          background-color: #f8f9fa;
          border-color: #007bff;
          transform: translateY(-1px);
          box-shadow: 0 2px 4px rgba(0,0,0,0.1);
        }
        .list-group-item.active {
          background-color: #007bff;
          border-color: #007bff;
          color: white;
        }
        .list-group-item.active .text-muted {
          color: rgba(255,255,255,0.8) !important;
        }
        .list-group-item.active .text-success {
          color: #90ee90 !important;
        }
        .message-bubble {
          max-width: 70%;
          padding: 12px 16px;
          border-radius: 18px;
          margin: 8px 0;
          word-wrap: break-word;
          position: relative;
        }
        .message-bubble.client {
          background: #f8f9fa;
          color: #212529;
          border: 1px solid #dee2e6;
          margin-left: auto;
          border-bottom-right-radius: 6px;
        }
        .message-bubble.admin {
          background: #212529;
          color: #ffffff;
          border: 1px solid #495057;
          margin-right: auto;
          border-bottom-left-radius: 6px;
        }
        .message-bubble.temp {
          opacity: 0.7;
          position: relative;
        }
        .message-bubble.temp::after {
          content: "Sending...";
          position: absolute;
          top: -20px;
          right: 0;
          font-size: 10px;
          color: #a0aec0;
          font-style: italic;
        }
        .chat-input {
          border: 1px solid #e2e8f0;
          border-radius: 12px;
          padding: 12px 16px;
          background: white;
          transition: all 0.3s ease;
        }
        .chat-input:focus {
          outline: none;
          border-color: #007bff;
          box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1);
        }
      `}</style>

       
      {/* Hidden file input */}
      <input 
        ref={fileRef}
        type="file" 
        onChange={uploadFile}
        accept="image/*,application/pdf,.doc,.docx,.txt"
        style={{ display: 'none' }}
      />
      <Row>
        <Col>
          <div className="list row">
            <div className="col-md-6 admin-visible">
              <h4 style={{ color: "#333" }}>Users List</h4>
              <ul className="list-group">
                {users &&
                  users.map((user, index) => (
                    <li
                      className={
                        "list-group-item " +
                        (index === currentIndex ? "active" : "")
                      }
                      onClick={() => setActiveUser(user, index)}
                      key={index}
                      style={{ cursor: 'pointer' }}
                    >
                      <div className="d-flex justify-content-between align-items-center">
                        <div>
                          <div className="fw-bold">
                            {user.firstName} {user.lastName}
                          </div>
                          <div className="text-muted small">
                            {user.email}
                          </div>
                        </div>
                        <div className="text-end">
                          
                          <div
                            className="d-flex justify-content-end align-items-center"
                            style={{ gap: '0.5rem', marginTop: '0.25rem' }}
                          >
                            {unreadCounts[user.id] > 0 && (
                              <Badge color="danger">
                                {unreadCounts[user.id]}
                              </Badge>
                            )}
                            <button
                              color="outline-danger"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                deleteChatHistory(user.id, `${user.firstName} ${user.lastName}`);
                              }}
                              title="Delete chat history"
                              style={{ fontSize: '12px' }}
                            >
                              🗑️
                            </button>
                          </div>
                        </div>
                      </div>
                    </li>
                  ))}
              </ul>
            </div>
            <div className="col-md-6 admin-visible">
              {currentUser && selectedConversation ? (
                <div>
                  <h4 style={{ color: "#333" }}>Chat with {currentUser.firstName} {currentUser.lastName}</h4>
                  
                  <div className="mb-3">
                    <small className="text-muted">Email: {currentUser.email}</small>
                  </div>
                  <div className="mb-3">
                    <small className="text-muted">Outstanding Payment: ${currentUser.pendingPayment}</small>
                  </div>

                  <div
                    className="border rounded p-3"
                    style={{ height: '400px', overflowY: 'auto', background: '#f8f9fa' }}
                  >
                    {messages.map((message) => (
                      <div
                        key={message.id}
                        className={`d-flex mb-3 ${
                          message.senderId === selectedConversation.clientId ? 'justify-content-start' : 'justify-content-end'
                        }`}
                      >
                        <div
                          className={`message-bubble ${
                            message.senderId === selectedConversation.clientId ? 'client' : 'admin'
                          } ${message.isTemp ? 'temp' : ''}`}
                        >
                          {message.type === 'text' ? (
                            <div>{message.content}</div>
                          ) : (
                            <div>
                              {message.mimeType?.startsWith('image/') ? (
                                <div>
                                  <img
                                    src={`${CHAT_API_URL}${message.fileUrl.replace('/api/chat/', '')}`}
                                    alt={message.fileName}
                                    style={{ 
                                      maxWidth: '200px', 
                                      maxHeight: '200px',
                                      borderRadius: '8px',
                                      cursor: 'pointer',
                                      objectFit: 'cover'
                                    }}
                                    onClick={() => window.open(`${CHAT_API_URL}${message.fileUrl.replace('/api/chat/', '')}`, '_blank')}
                                  />
                                </div>
                              ) : (
                                <a
                                  href={`${CHAT_API_URL}${message.fileUrl.replace('/api/chat/', '')}`}
                                  target="_blank"
                                  rel="noreferrer"
                                  className={message.senderId === selectedConversation.clientId ? 'text-dark' : 'text-white'}
                                  style={{ textDecoration: 'none' }}
                                >
                                  📎 {message.fileName}
                                </a>
                              )}
                            </div>
                          )}
                          <small
                            className="d-block mt-2"
                            style={{ 
                              opacity: 0.7,
                              fontSize: '11px'
                            }}
                          >
                            {formatDate(message.createdAt)}
                          </small>
                        </div>
                      </div>
                    ))}
                    <div ref={messagesEndRef} />
                  </div>

                  {selectedConversation.status !== 'closed' && (
                    <div className="p-3 border-top" style={{ background: 'white' }}>
                      <div className="d-flex gap-2 align-items-center">
                         {/* File upload button */}
                        <button
                          className="file-upload-btn"
                          onClick={() => fileRef.current?.click()}
                          title="Attach file"
                          type="button"
                        >
                          📎
                        </button>
                        
                        <input
                          type="text"
                          className="form-control chat-input"
                          placeholder="Type your message..."
                          value={newMessage}
                          onChange={(e) => setNewMessage(e.target.value)}
                          onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                        />
                        <Button 
                          color="primary" 
                          onClick={sendMessage}
                          style={{ 
                            backgroundColor: '#007bff', 
                            border: 'none', 
                            padding: '0 20px', 
                            fontWeight: 'bold', 
                            fontSize: '15px', 
                            borderRadius: '4px',
                            minWidth: '70px',
                            boxShadow: 'none'
                          }}
                        >
                          Send
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div>
                  <br />
                  <p>Please click on a User to start chatting...</p>
                </div>
              )}
            </div>
          </div>
        </Col>
      </Row>
    </div>
  );
}
